import React, { useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  Collapse,
  Button,
  useTheme
} from '@mui/material'
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { useNotificationLogs } from '../../hooks/useNotifications'

const NotificationLogs = () => {
  const theme = useTheme()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [filters, setFilters] = useState({
    type: '',
    success: '',
    startDate: null,
    endDate: null
  })
  const [showFilters, setShowFilters] = useState(false)
  const [expandedRow, setExpandedRow] = useState(null)

  // Build query parameters
  const queryParams = {
    page: page + 1,
    limit: rowsPerPage,
    ...(filters.type && { type: filters.type }),
    ...(filters.success !== '' && { success: filters.success }),
    ...(filters.startDate && { startDate: filters.startDate.format('YYYY-MM-DD') }),
    ...(filters.endDate && { endDate: filters.endDate.format('YYYY-MM-DD') })
  }

  const { data, isLoading, error, refetch } = useNotificationLogs(queryParams)
  const { logs = [], pagination = {} } = data || {}

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage)
  }

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPage(0) // Reset to first page when filtering
  }

  // Clear filters
  const clearFilters = () => {
    setFilters({
      type: '',
      success: '',
      startDate: null,
      endDate: null
    })
    setPage(0)
  }

  // Toggle row expansion
  const toggleRowExpansion = (logId) => {
    setExpandedRow(expandedRow === logId ? null : logId)
  }

  // Get notification type label
  const getNotificationTypeLabel = (type) => {
    const types = {
      'weekly_reminder': 'Recordatorio Semanal',
      'overdue_alert': 'Alerta de Vencidas',
      'monthly_report': 'Reporte Mensual',
      'payment_received': 'Pago Recibido',
      'new_invoice': 'Nueva Factura',
      'test': 'Prueba'
    }
    return types[type] || type
  }

  // Get notification type color
  const getNotificationTypeColor = (type) => {
    const colors = {
      'weekly_reminder': 'primary',
      'overdue_alert': 'error',
      'monthly_report': 'info',
      'payment_received': 'success',
      'new_invoice': 'warning',
      'test': 'secondary'
    }
    return colors[type] || 'default'
  }

  // Get notification type color with dark theme support
  // Función para obtener colores de chips adaptativos al tema
  const getNotificationTypeColorWithTheme = (type, theme) => {
    const isDark = theme.palette.mode === 'dark'
    
    if (isDark) {
      const darkColors = {
        'weekly_reminder': 'primary',
        'overdue_alert': 'error',
        'monthly_report': 'info',
        'payment_received': 'success',
        'new_invoice': 'warning',
        'test': 'secondary'
      }
      return darkColors[type] || 'default'
    }
    
    return getNotificationTypeColor(type)
  }

  // Format date
  const formatDate = (dateString) => {
    return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss')
  }

  // Truncate text
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-'
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
  }

  // Get statistics
  const getStats = () => {
    if (!logs.length) return null

    const total = logs.length
    const successful = logs.filter(log => log.success).length
    const failed = total - successful
    const successRate = ((successful / total) * 100).toFixed(1)

    return { total, successful, failed, successRate }
  }

  const stats = getStats()

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error al cargar los logs de notificaciones: {error.message}
      </Alert>
    )
  }

  return (
    <Box>
      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Enviados
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">
                  {stats.successful}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Exitosos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main">
                  {stats.failed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fallidos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="info.main">
                  {stats.successRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tasa de Éxito
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Historial de Notificaciones
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              size="small"
            >
              Actualizar
            </Button>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
            >
              Filtros
            </Button>
          </Box>
        </Box>

        <Collapse in={showFilters}>
          <Box sx={{ p: 2, pt: 0, borderTop: 1, borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    label="Tipo"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="weekly_reminder">Recordatorio Semanal</MenuItem>
                    <MenuItem value="overdue_alert">Alerta de Vencidas</MenuItem>
                    <MenuItem value="monthly_report">Reporte Mensual</MenuItem>
                    <MenuItem value="payment_received">Pago Recibido</MenuItem>
                    <MenuItem value="new_invoice">Nueva Factura</MenuItem>
                    <MenuItem value="test">Prueba</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filters.success}
                    onChange={(e) => handleFilterChange('success', e.target.value)}
                    label="Estado"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Exitosos</MenuItem>
                    <MenuItem value="false">Fallidos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Desde"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Hasta"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button onClick={clearFilters} size="small">
                  Limpiar Filtros
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Destinatarios</TableCell>
              <TableCell>Asunto</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>Cargando logs...</Typography>
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No hay logs de notificaciones
                    </Typography>
                    <Typography color="text.secondary">
                      Los logs aparecerán aquí cuando se envíen notificaciones
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <React.Fragment key={log.id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(log.sentAt)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={getNotificationTypeLabel(log.notificationType)}
                        color={getNotificationTypeColorWithTheme(log.notificationType, theme)}
                        size="small"
                            sx={{
                              ...(theme.palette.mode === 'dark' && {
                                '&.MuiChip-colorPrimary': {
                                  backgroundColor: theme.palette.primary.dark,
                                  color: theme.palette.primary.contrastText
                                },
                                '&.MuiChip-colorError': {
                                  backgroundColor: theme.palette.error.dark,
                                  color: theme.palette.error.contrastText
                                },
                                '&.MuiChip-colorInfo': {
                                  backgroundColor: theme.palette.info.dark,
                                  color: theme.palette.info.contrastText
                                },
                                '&.MuiChip-colorSuccess': {
                                  backgroundColor: theme.palette.success.dark,
                                  color: theme.palette.success.contrastText
                                },
                                '&.MuiChip-colorWarning': {
                                  backgroundColor: theme.palette.warning.dark,
                                  color: theme.palette.warning.contrastText
                                },
                                '&.MuiChip-colorSecondary': {
                                  backgroundColor: theme.palette.secondary.dark,
                                  color: theme.palette.secondary.contrastText
                                }
                              })
                            }}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {log.success ? (
                          <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        ) : (
                          <ErrorIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                        )}
                        <Typography variant="body2">
                          {log.success ? 'Exitoso' : 'Fallido'}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {log.recipients.length} destinatario{log.recipients.length !== 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {truncateText(log.subject)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Tooltip title={expandedRow === log.id ? 'Contraer' : 'Expandir detalles'}>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(log.id)}
                        >
                          {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row Details */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0 }}>
                      <Collapse in={expandedRow === log.id}>
                        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                Destinatarios:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {log.recipients.map((email, index) => (
                                  <Chip
                                    key={index}
                                    label={email}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      ...(theme.palette.mode === 'dark' && {
                                        borderColor: theme.palette.divider,
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                          backgroundColor: theme.palette.action.hover
                                        }
                                      })
                                    }}
                                  />
                                ))}
                              </Box>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                Asunto Completo:
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {log.subject}
                              </Typography>
                            </Grid>
                            
                            {log.errorMessage && (
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom color="error">
                                  Error:
                                </Typography>
                                <Typography variant="body2" color="error">
                                  {log.errorMessage}
                                </Typography>
                              </Grid>
                            )}
                            
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" gutterBottom>
                                Contenido del Email:
                              </Typography>
                              <Paper
                                sx={{
                                  p: 2,
                                  maxHeight: 200,
                                  overflow: 'auto',
                                  bgcolor: 'background.paper',
                                  border: 1,
                                  borderColor: 'divider'
                                }}
                              >
                                <Box
                                  dangerouslySetInnerHTML={{ __html: log.content }}
                                  sx={{
                                    fontSize: '0.8rem',
                                    color: 'text.primary',
                                    '& *': {
                                      color: 'inherit !important'
                                    },
                                    '& p, & div, & span, & td, & th': {
                                      color: 'inherit !important'
                                    },
                                    // Estilos específicos para tablas en el contenido del email
                                    '& table': {
                                      width: '100%',
                                      borderCollapse: 'collapse',
                                      backgroundColor: 'transparent !important'
                                    },
                                    '& th': {
                                      backgroundColor: '#0C2E59 !important',
                                      color: '#ffffff !important',
                                      padding: '8px',
                                      border: '1px solid',
                                      borderColor: '#ffffff !important',
                                      fontWeight: 'bold'
                                    },
                                    '& td': {
                                      backgroundColor: 'background.paper !important',
                                      color: 'text.primary !important',
                                      padding: '8px',
                                      border: '1px solid',
                                      borderColor: '#ffffff !important'
                                    },
                                    '& tr:nth-of-type(even) td': {
                                      backgroundColor: 'action.hover !important'
                                    },
                                    '& tr:hover td': {
                                      backgroundColor: 'action.selected !important'
                                    },
                                    // Estilos para tablas simples (sin thead, tbody, tfoot)
                                    // Estilos para la última fila de la tabla (totales)
                                    '& tr:last-child td, & tr:last-child th': {
                                      backgroundColor: 'background.paper !important',
                                      color: 'text.primary !important',
                                      border: '1px solid',
                                      borderColor: '#ffffff !important',
                                      fontWeight: 'bold'
                                    },
                                    // Estilos para totales y resúmenes
                                    '& p, & div, & span': {
                                      backgroundColor: 'background.paper !important',
                                      color: 'text.primary !important'
                                    },
                                    '& strong, & b': {
                                      color: 'text.primary !important',
                                      fontWeight: 'bold'
                                    },
                                    // Estilos específicos para elementos de resumen
                                    '& p:last-child, & div:last-child': {
                                      backgroundColor: 'background.paper !important',
                                      color: 'text.primary !important',
                                      padding: '8px',
                                      borderRadius: '4px',
                                      marginTop: '8px'
                                    }
                                  }}
                                />
                              </Paper>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {logs.length > 0 && (
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        )}
      </TableContainer>
    </Box>
  )
}

export default NotificationLogs
